import { zodResolver } from '@hookform/resolvers/zod';
import { equals, includes } from 'ramda';
import React, { FC, useContext, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import styled from 'styled-components';
import { z } from 'zod';

import BasicText from '@ui/atoms/BasicText';
import Button from '@ui/atoms/Button';
import FlexColumn from '@ui/atoms/FlexColumn';
import HeadingText from '@ui/atoms/HeadingText';
import SelectableButton from '@ui/atoms/SelectableButton';
import SelectableButtonGrid from '@ui/atoms/SelectableButtonGrid';
import SelectableButtonGroup from '@ui/atoms/SelectableButtonGroup';

import CenteredGridItem from '@ui/molecules/CenteredGridItem';
import SubmitButton from '@ui/molecules/SubmitButton';

import FormInputWrapper from '@ui/organisms/FormInputWrapper';

import useResetField from '@hooks/useResetField';

import { commonErrors } from '@utils/error';
import { getValuesFromData } from '@utils/general';
import { boolRadioButtonString } from '@utils/validation';

import FlowContext from '@providers/FlowContextProvider';

import { FormData, IFlowStepComponentProps } from '@folx-types/flow-steps';
import { SpacingTypes } from '@folx-types/theme';

import theme from '@theme/FolxTheme';

import { dataConstants, schema } from './ExceptionalConditionsStep.data';

export interface IExceptionalConditionsStepPageContent {
	pageHeader: string;
	pageSubheader: string;
	suicidalThoughtsQuestion: string;
	severityQuestion: string;
	vagueThoughtsAnswer: string;
	specificPlanAnswer: string;
}
interface IExceptionalConditionsStepProps extends IFlowStepComponentProps {
	pageContent: IExceptionalConditionsStepPageContent;
}

const BinarySelectableButton = styled(SelectableButton)`
	width: 100%;
	@media ${theme.responsive.breakpoints.tablet} {
		width: ${theme.spacing.xxxxxl};
	}
`;

const TopSection = styled(CenteredGridItem)`
	> ${HeadingText} {
		margin-bottom: ${theme.spacing.sm};
	}
`;

const SpacedGridItem = styled(CenteredGridItem)<{
	marginBottom: SpacingTypes;
}>`
	margin-bottom: ${({ marginBottom }) => theme.spacing[marginBottom]};
`;

const NavigationWrapper = styled.div`
	align-items: center;
	display: flex;
	gap: ${theme.spacing.sm};

	@media ${theme.responsive.breakpoints.tablet} {
		justify-content: flex-end;

		${Button} {
			max-width: 15rem;
		}
	}
`;

const QuestionColumn = styled(FlexColumn)`
	margin-top: ${theme.spacing.lgxl};
	margin-bottom: ${theme.spacing.xxl};
	gap: ${theme.spacing.lgxl};
	justify: center;
	${SelectableButton} {
		width: 100%;
	}
`;

const ExceptionalConditionsStep: FC<IExceptionalConditionsStepProps> = ({
	stepKey,
	pageContent,
}) => {
	const {
		pageHeader,
		pageSubheader,
		suicidalThoughtsQuestion,
		severityQuestion,
		vagueThoughtsAnswer,
		specificPlanAnswer,
	} = pageContent;

	const [submitting, setSubmitting] = useState(false);

	const context = useContext(FlowContext);
	const { handleFormSubmission, handleNavBack, existingFormData } = context;

	type Schema = Partial<z.input<typeof schema>>;
	const { handleSubmit, control, formState, reset, watch, setValue } = useForm<Schema>({
		resolver: zodResolver(schema),
		mode: 'onChange',
		defaultValues: {
			...getValuesFromData(dataConstants, existingFormData),
			[dataConstants.SUICIDAL_THOUGHTS]: boolRadioButtonString(
				existingFormData?.[dataConstants.SUICIDAL_THOUGHTS] as boolean
			),
			[dataConstants.SEVERITY]: undefined,
		},
	});
	const { isValid, isSubmitting, errors } = formState;

	const hasSuicidalThoughts = includes(watch(dataConstants.SUICIDAL_THOUGHTS), ['1', '2', '3']);
	useResetField<boolean>({
		fieldValue: hasSuicidalThoughts ? false : true,
		condition: equals(false as boolean),
		fields: [dataConstants.SEVERITY],
		setValue,
	});

	const onSubmit = async (data: FormData) => {
		setSubmitting(true);
		let formData = getValuesFromData(dataConstants, data);
		formData = {
			...formData,
			[dataConstants.SUICIDE_RISK]:
				formData?.[dataConstants.SEVERITY] === dataConstants.SPECIFIC_PLAN,
		};
		await handleFormSubmission(formData, stepKey);
		setSubmitting(false);
	};

	return (
		<>
			<TopSection>
				<HeadingText>{pageHeader}</HeadingText>
				<BasicText>{pageSubheader}</BasicText>
			</TopSection>
			<CenteredGridItem>
				<form onSubmit={handleSubmit(onSubmit)}>
					<QuestionColumn>
						<FormInputWrapper
							variant="full"
							subheader={suicidalThoughtsQuestion}
							error={commonErrors.selectionRequired}
							showError={!!errors?.suicidalThoughts}
							groupName={dataConstants.SUICIDAL_THOUGHTS}>
							<Controller
								name={dataConstants.SUICIDAL_THOUGHTS}
								control={control}
								render={({ field: { value, ...field } }) => (
									<SelectableButtonGroup layout="column" name={dataConstants.SUICIDAL_THOUGHTS}>
										<BinarySelectableButton optionValue={'0'} value={value ?? ''} {...field}>
											Not at all
										</BinarySelectableButton>
										<BinarySelectableButton optionValue={'1'} value={value ?? ''} {...field}>
											Several days
										</BinarySelectableButton>
										<BinarySelectableButton optionValue={'2'} value={value ?? ''} {...field}>
											More than half the days
										</BinarySelectableButton>
										<BinarySelectableButton optionValue={'3'} value={value ?? ''} {...field}>
											Nearly every day
										</BinarySelectableButton>
									</SelectableButtonGroup>
								)}
							/>
						</FormInputWrapper>
						{hasSuicidalThoughts && (
							<FormInputWrapper
								variant="full"
								subheader={severityQuestion}
								error={commonErrors.selectionRequired}
								showError={!!errors?.[dataConstants.SEVERITY]}
								groupName={dataConstants.SEVERITY}>
								<Controller
									name={dataConstants.SEVERITY}
									control={control}
									render={({ field: { value, ...field } }) => (
										<SelectableButtonGrid
											role="radiogroup"
											mobileColumns={1}
											desktopColumns={1}
											aria-label={dataConstants.SEVERITY}>
											<SelectableButton
												optionValue={dataConstants.VAGUE_THOUGHTS}
												value={value ?? ''}
												{...field}>
												{vagueThoughtsAnswer}
											</SelectableButton>
											<SelectableButton
												optionValue={dataConstants.SPECIFIC_PLAN}
												value={value ?? ''}
												{...field}>
												{specificPlanAnswer}
											</SelectableButton>
										</SelectableButtonGrid>
									)}
								/>
							</FormInputWrapper>
						)}
					</QuestionColumn>
					<SpacedGridItem marginBottom="xxxl">
						<NavigationWrapper>
							<Button label="Back" onClick={() => handleNavBack(stepKey)} />
							<SubmitButton variant="secondary" label="Next" submitting={submitting} />
						</NavigationWrapper>
					</SpacedGridItem>
				</form>
			</CenteredGridItem>
		</>
	);
};

export default ExceptionalConditionsStep;
